import api from './api';
import { nullableUuid } from '@/utils/uuid';

export type QualidadeTipo = 'ENTRADA' | 'PROCESSO' | 'FINAL';
export type QualidadeResultado = 'APROVADO' | 'REPROVADO' | 'APROVADO_COM_RESSALVAS';

export interface QualidadeChecklistItem {
  parametro: string;
  valor_medido?: string;
  limite_min?: string;
  limite_max?: string;
  ok?: boolean;
  observacao?: string;
}

export interface QualidadeInspecao {
  id: string;
  tipo: QualidadeTipo;
  lote_id?: string | null;
  lote_numero?: string | null;
  op_id?: string | null;
  op_numero?: string | null;
  produto_codigo?: string | null;
  produto_nome?: string | null;
  data_inspecao: string;
  resultado: QualidadeResultado;
  checklist?: QualidadeChecklistItem[] | null;
  acao_corretiva?: string | null;
  responsavel?: string | null;
  observacoes?: string | null;
}

export interface QualidadeInspecaoInput {
  tipo: QualidadeTipo;
  lote_id?: string | null;
  op_id?: string | null;
  data_inspecao?: string | null;
  resultado: QualidadeResultado;
  checklist?: QualidadeChecklistItem[];
  acao_corretiva?: string | null;
  responsavel?: string | null;
  observacoes?: string | null;
}

const ENDPOINT = '/industrializacao/qualidade-inspecao';

const qualidadeInspecaoService = {
  async listar(filters: { tipo?: string; resultado?: string; lote?: string; op?: string } = {}): Promise<QualidadeInspecao[]> {
    const response = await api.get<QualidadeInspecao[]>(ENDPOINT, { params: filters });
    return response.data;
  },
  async buscarPorId(id: string): Promise<QualidadeInspecao> {
    const response = await api.get<QualidadeInspecao>(`${ENDPOINT}/${id}`);
    return response.data;
  },
  async criar(data: QualidadeInspecaoInput): Promise<QualidadeInspecao> {
    const response = await api.post<QualidadeInspecao>(ENDPOINT, {
      ...data,
      lote_id: nullableUuid(data.lote_id),
      op_id: nullableUuid(data.op_id),
    });
    return response.data;
  },
  async atualizar(id: string, data: Partial<QualidadeInspecaoInput>): Promise<QualidadeInspecao> {
    const response = await api.put<QualidadeInspecao>(`${ENDPOINT}/${id}`, {
      ...data,
      lote_id: data.lote_id === undefined ? undefined : nullableUuid(data.lote_id),
      op_id: data.op_id === undefined ? undefined : nullableUuid(data.op_id),
    });
    return response.data;
  },
  async excluir(id: string): Promise<void> {
    await api.delete(`${ENDPOINT}/${id}`);
  },
  /** Aprova a inspeção e libera o lote (status LIVRE) */
  async aprovar(id: string): Promise<QualidadeInspecao> {
    const response = await api.post<QualidadeInspecao>(`${ENDPOINT}/${id}/aprovar`);
    return response.data;
  },
  /** Reprova a inspeção e bloqueia o lote (status BLOQUEADO) */
  async reprovar(id: string, acaoCorretiva: string): Promise<QualidadeInspecao> {
    const response = await api.post<QualidadeInspecao>(`${ENDPOINT}/${id}/reprovar`, { acao_corretiva: acaoCorretiva });
    return response.data;
  },
};

export default qualidadeInspecaoService;
